/**
 * Created by rathompson on 10/13/23
 * File: MoveTenantPreviewTable.tsx
 * Project: cloud-mgmt-modules ©
 * ©
 */


import type { VFC } from 'react'
import V5TableWrapper from '../../../common/components/table/V5TableWrapper'
import { ConnectedTable, ConnectedTableFilter, ConnectedTableLayout, TableProvider } from '@panwds/react-table'
import { useContext, useEffect, useMemo, useState } from 'react'
import { LABEL_LOST_ACCESS_SUBTITLE, LABEL_LOST_ACCESS_TITLE, TABLE_HEADER_APPLICATIONS, TABLE_HEADER_DISPLAY_NAME, TABLE_HEADER_ROLES } from '../../constants/LabelConstants'
import type { IAM_AccessPolicy } from '../../../common/reducers/types/IAMReducer.types'
import { getMembers } from '../../../iam/components/identities/table/IdentitiesTable/utils'
import GlobalContext from '../../../common/context/GlobalContext'
import { safeSet } from '../../../common/utils/Utils'
import { Body, useIsMounted } from '@panwds/react-ui'
import RoleRenderer from '../../../iam/components/identities/table/cellRenderer/RoleRenderer'
import ApplicationsRenderer from '../../../iam/components/identities/table/cellRenderer/ApplicationsRenderer'
import styled from 'styled-components'
import TableHeader from '../../../common/components/table/TableHeader'
import ConnectedTableDownloadButton from '../../../common/components/table/ConnectedTableDownloadButton'
import IAMAppName from '../../../iam/utils/IAMAppName'
import { LABEL_ALL_APPS_SERVICES } from '../../../iam/constants/LabelConstants'
import AppContext from '../../../common/context/AppContext'

const CONTAINER = styled(V5TableWrapper)`
  [role="table"] {
    max-height: 300px;
    overflow: auto;
  }
`

const MoveTenantPreviewTable: VFC<{
  policies: IAM_AccessPolicy[],
  loading: boolean
}> = ({ policies, loading }) => {

  const { treeReducer, iamReducer } = useContext(GlobalContext)
  const { getAppName } = useContext(AppContext)
  const { sgKeyBy: tsgMap } = treeReducer
  const isMounted = useIsMounted()
  const { roleMap = {}, customRoleMap = {} } = iamReducer

  const [data, setData] = useState([])

  const columns = useMemo(() => {
    return [
      {
        id: 'display_name',
        Header: TABLE_HEADER_DISPLAY_NAME,
        accessor: 'display_name',
        Cell: ({ value }: any) => (
          <div className='cm-p-1 cm-w-full cm-h-full'>
            <Body size='lg' addClassName='cm-truncate'>{value}</Body>
          </div>
        )
      },
      {
        id: 'applications',
        Header: TABLE_HEADER_APPLICATIONS,
        Cell: ApplicationsRenderer,
        width: 180,
        accessor: (row: any) => {
          if (row?.roles) {
            return Object.entries(row?.roles).sort().map(([app_id, policies]: any) => {
              return policies?.map(({ role }: any, index: any) => {
                return index === 0 ? app_id ? IAMAppName(app_id, getAppName) : LABEL_ALL_APPS_SERVICES : ''
              })
            }) as any
          }
          return ''
        }
      },
      {
        id: 'roles',
        Header: TABLE_HEADER_ROLES,
        Cell: RoleRenderer,
        accessor: (row: any) => {
          if (row?.roles) {
            return Object.entries(row?.roles).sort().map(([app_id, policies]: any) => {
              return policies?.map(({ role }: any) => customRoleMap[role]?.label || customRoleMap[role]?.name || roleMap[role]?.label || role)
            }) as any
          }
          return ''
        }
      }
    ]
  }, [customRoleMap, getAppName, roleMap])

  useEffect(() => {
    safeSet(setData, getMembers(policies, [], tsgMap), isMounted.current)
  }, [isMounted, policies, tsgMap])

  return (
    <CONTAINER>
      <TableProvider
        isLoading={loading}
        enableColumnOrder
        enableColumnSort
        enableResizeColumns
        enablePagination
        columns={columns as any}
        data={data}
      >
        <TableHeader
          title={LABEL_LOST_ACCESS_TITLE}
          subtitle={LABEL_LOST_ACCESS_SUBTITLE}
        >
          <div className='cm-flex cm-gap-2'>
            <ConnectedTableFilter />
            <ConnectedTableDownloadButton fileName={'users'} />
          </div>
        </TableHeader>
        <ConnectedTableLayout>
          <ConnectedTable/>
        </ConnectedTableLayout>
      </TableProvider>
    </CONTAINER>
  )
}

export default MoveTenantPreviewTable
